/**
 * Groupings of a unit's hardware commands for the site-dashboard UnitCard.
 * Both the pending and completed command entities present the same two views:
 * commands grouped by the site they target (newest-first, labelled with the
 * admin or end-user wording of each command), and commands grouped by the bulk
 * command that spawned them (admin wording). This module holds that shared
 * grouping logic so the pending and completed selector modules stay identical
 * except for the base slice they read.
 *
 * Contains:
 * - buildUnitToSiteMapping — commands → Map(siteId → newest-first command List)
 * - buildUnitToBulkCommandMapping — commands → Map(bulkCommandId → command List)
 *
 * Returns immutable.js Maps/Lists (the shape UnitCard consumes) — pure, no redux.
 */

import { List, Map } from 'immutable';
import moment from 'moment';

import { getAdminCommandName, getUserCommandName } from '@sophocles/utils/commandUtils';

/**
 * @param {import('immutable').Iterable} commands - the command entity collection.
 * @param {import('immutable').Map} user - the authenticated user; `is_staff`
 *   selects admin vs end-user command wording.
 * @returns {import('immutable').Map} siteId (string) → List of commands (each
 *   with a `displayName`), sorted newest-first by `created_time`.
 */
export function buildUnitToSiteMapping(commands, user) {
  let myMap = Map();

  const isStaff = user.get('is_staff', false);

  const displayNameFactory = isStaff ? getAdminCommandName : getUserCommandName;

  commands.forEach(command => {
    const site_id = command.get('site', '0').toString();
    const displayName = displayNameFactory(command);
    if (displayName != null) {
      command = Map(command).set('displayName', displayName);
      myMap = myMap.set(site_id, myMap.get(site_id, List()).push(command));
    }
  });

  return myMap.map(commandList =>
    commandList.sort((a, b) => (moment(a.get('created_time')).isBefore(moment(b.get('created_time'))) ? 1 : -1)),
  );
}

/**
 * @param {import('immutable').Iterable} commands - the command entity collection.
 * @returns {import('immutable').Map} bulkCommandId (string) → List of commands
 *   (each with an admin-wording `displayName`).
 */
export function buildUnitToBulkCommandMapping(commands) {
  let myMap = Map();

  const displayNameFactory = getAdminCommandName;

  commands.forEach(command => {
    const bulk_id = command.get('bulk_command', '0') + '';
    const displayName = displayNameFactory(command);
    if (displayName != null) {
      command = Map(command).set('displayName', displayName);
      myMap = myMap.set(bulk_id, myMap.get(bulk_id, List()).push(command));
    }
  });

  return myMap;
}
